/**
 * EventTypeBadge - Compact pill badge that distinguishes Work Order
 * events from Inspection events on the unified Calendar and Map views.
 *
 * Single responsibility: render the type with a stable color +
 * abbreviation so that, glanced quickly, the user can tell which kind
 * of record they're looking at without reading the full title.
 *
 * Used by:
 *   - EventListItem (sidebar list)
 *   - EventDetailsPopover (header)
 *   - CalendarView event chips (custom event renderer)
 *   - MapView popups
 *
 * @example
 * <EventTypeBadge type="work_order" />
 * <EventTypeBadge type="inspection" size="sm" />
 */
import React from 'react';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * Categories the badge knows how to render. Aligns with
 * ScheduledEventCategory in the service layer — wire `recordType` is
 * insufficient because "project work order" is a derived sub-type of
 * `work_order` that the calendar treats distinctly.
 */
export type EventTypeBadgeType = 'work_order' | 'project_work_order' | 'inspection';

/** Supported size variants. */
export type EventTypeBadgeSize = 'xs' | 'sm';

/**
 * Props for EventTypeBadge.
 *
 * @property type      Which record type to badge
 * @property size      Visual scale; defaults to 'sm'
 * @property className Optional extra classes (merged after defaults)
 */
export interface EventTypeBadgeProps {
    type: EventTypeBadgeType;
    size?: EventTypeBadgeSize;
    className?: string;
}

/* ============================================
 * STYLE LOOKUP
 * ============================================
 * Static maps so the same type always renders with the same colors —
 * no runtime branching beyond a single dictionary read.
 */

const TYPE_LABEL: Record<EventTypeBadgeType, string> = {
    work_order: 'Work Order',
    project_work_order: 'Project',
    inspection: 'Inspection',
};

/* Color palettes chosen for accessibility on the calendar/map surfaces:
 * - Work Order         : brand-primary tint (orange — primary CTA color)
 * - Project Work Order : deeper burnt-orange tint — same family as
 *                        standard WOs (project WOs *are* work orders)
 *                        but darker so the chip reads as a distinct
 *                        sub-type on shared surfaces
 * - Inspection         : blue family (reads as "review/check") */
const TYPE_COLORS: Record<EventTypeBadgeType, string> = {
    work_order: 'bg-brand-primary/10 text-brand-primary border-brand-primary/30',
    project_work_order: 'bg-orange-100 text-orange-800 border-orange-300',
    inspection: 'bg-blue-50 text-blue-700 border-blue-200',
};

const SIZE_CLASSES: Record<EventTypeBadgeSize, string> = {
    xs: 'text-10px px-6px py-1px',
    sm: 'text-11px px-8px py-2px',
};

/* ============================================
 * COMPONENT
 * ============================================ */

export const EventTypeBadge: React.FC<EventTypeBadgeProps> = ({
    type,
    size = 'sm',
    className,
}) => {
    const classes = [
        `event-type-badge event-type-badge--${type}`,
        'inline-flex items-center font-sans font-medium uppercase tracking-wide rounded border',
        TYPE_COLORS[type],
        SIZE_CLASSES[size],
        className ?? '',
    ].filter(Boolean).join(' ');

    return (
        <span className={classes} data-ref="atom-event-type-badge" aria-label={`${TYPE_LABEL[type]} event`}>
            {TYPE_LABEL[type]}
        </span>
    );
};

export default EventTypeBadge;
